import { HardwareService } from '../../HardwareService';

/** Why a camera stream could not be opened. Callers map these to operator-facing copy. */
export type CameraStreamErrorKind = 'insecure' | 'denied' | 'notFound' | 'busy' | 'unknown';

export type CameraStreamResult =
  | { stream: MediaStream; error?: undefined }
  | { stream?: undefined; error: CameraStreamErrorKind };

/** 1080p: the getUserMedia default is often 640x480, too few pixels per module for any decoder. */
const SCAN_VIDEO_CONSTRAINTS: MediaTrackConstraints = {
  facingMode: 'environment',
  width: { ideal: 1920 },
  height: { ideal: 1080 },
};

const kindFromError = (error: unknown): CameraStreamErrorKind => {
  const name = error instanceof Error ? error.name : '';

  if (name === 'NotAllowedError' || name === 'PermissionDeniedError' || name === 'SecurityError') {
    return 'denied';
  }
  if (name === 'NotFoundError' || name === 'DevicesNotFoundError' || name === 'OverconstrainedError') {
    return 'notFound';
  }

  return name === 'NotReadableError' || name === 'TrackStartError' ? 'busy' : 'unknown';
};

/**
 * Opens the rear camera. **Call from the click handler**: iOS Safari wants a user gesture. The caller
 * owns the stream and must stop its tracks. Outside a secure context `mediaDevices` is undefined, so
 * it is detected rather than called.
 */
export const requestRearCameraStream = async (): Promise<CameraStreamResult> => {
  if (typeof navigator === 'undefined' || navigator.mediaDevices?.getUserMedia == null) {
    return { error: 'insecure' };
  }

  try {
    return { stream: await navigator.mediaDevices.getUserMedia({ video: SCAN_VIDEO_CONSTRAINTS }) };
  } catch (error) {
    HardwareService.logger.d('Camera stream could not be opened', { error });

    return { error: kindFromError(error) };
  }
};

/** Every exit path has to call this: an unstopped track leaves the camera indicator lit. */
export const stopCameraStream = (stream: MediaStream | null | undefined): void => {
  stream?.getTracks().forEach((track) => track.stop());
};
